import { useCallback, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { toast } from 'sonner'

import { useAuthInfo } from '@sweetauth/core'

import { editorPathname } from '@/helpers/document'
import {
  NOT_A_WORD_DOCUMENT_MESSAGE,
  isWordDocument,
  storedVersionOf,
} from '@/helpers/file'

import { buildDocumentsByFile } from '@/hooks/documents/linkedDocument'
import { useDocuments } from '@/hooks/documents/useDocuments'
import { useOpenWorkingCopy } from '@/hooks/documents/useOpenWorkingCopy'
import { type StoredDocx, fileQueryKeys } from '@/hooks/files/types'
import { fetchFile } from '@/hooks/files/useFile'
import { useCopyFilesToLibrary } from '@/hooks/organization/useCopyFilesToLibrary'
import { usePursuitFiles } from '@/hooks/pursuits/usePursuitFiles'

import type { PaginatedResponse } from '@/types/Api'
import type { File as SweetFile, PotentiallyUnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { Document } from '@/types/pursuit/Document'

const DOCX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

/** File input `accept` for the one format the editor can open. */
export const DOCX_ACCEPT = `.docx,${DOCX_CONTENT_TYPE}`

/**
 * Whether a stored file is a Word document the editor can open. Files uploaded
 * from some sources arrive without a content type, so the extension counts too.
 */
export const isDocxFile = (file: PotentiallyUnhydratedFile): boolean =>
  file.name.toLowerCase().endsWith('.docx') ||
  file.content_type === DOCX_CONTENT_TYPE

/**
 * Both selectors are defined out here so their identity is stable: React Query
 * re-runs a selector whose function changes, which would defeat the point of
 * deriving inside the cache rather than in a `useMemo` at the call site.
 */
const selectDocxFiles = (data: PaginatedResponse<SweetFile>): SweetFile[] =>
  data.data.filter(isDocxFile)

/** Shared empties, so a caller's list keeps its identity while a query loads. */
const NO_FILES: SweetFile[] = []
const NO_DOCUMENTS = new Map<UUID, never>()

interface UseDocxImportArgs {
  orgId: UUID | ''
  pursuitId: UUID | ''
  /**
   * Runs once navigation has been kicked off — close the menu or dialog the
   * open was started from. Must be stable across renders.
   */
  onNavigate?: () => void
  /** Skip the pursuit-file query until the surface actually needs the list. */
  enabled?: boolean
}

/**
 * Opening a stored .docx in the editor.
 *
 * A file opens as one document — its working copy, made on the first open and
 * pointed back at the file (`file_id`) — and every open after lands on the same one.
 *
 * A Library file is a stencil: it is copied into the pursuit first and the copy is
 * what opens, so the stencil stays as it is.
 */
export const useDocxImport = ({
  orgId,
  pursuitId,
  onNavigate,
  enabled = true,
}: UseDocxImportArgs) => {
  const router = useRouter()
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const { data: docxFiles = NO_FILES, isLoading: filesAreLoading } =
    usePursuitFiles({
      pursuitId,
      enabled,
      select: selectDocxFiles,
    })
  const { data: documentByFile = NO_DOCUMENTS } = useDocuments(pursuitId, {
    enabled,
    select: buildDocumentsByFile,
  })
  const { mutateAsync: openWorkingCopy } = useOpenWorkingCopy()
  const { mutateAsync: copyFiles } = useCopyFilesToLibrary({ orgId })

  // Tracked here rather than read off the mutations, which only cover the last
  // steps: the file is downloaded and validated first, and on a large file that
  // is most of the wait. A button bound to a mutation alone sits idle through
  // it, then shows its spinner just as the open finishes.
  const [isImporting, setIsImporting] = useState(false)

  const openDocument = useCallback(
    (document: Document) => {
      void router.push({
        pathname: editorPathname(document.tiptap_id),
        query: { orgId, pursuitId, documentId: document.id },
      })
      onNavigate?.()
    },
    [router, orgId, pursuitId, onNavigate],
  )

  /** The pursuit's own copy of a file from outside it, or null on failure. */
  const copyIntoPursuit = useCallback(
    async (file: PotentiallyUnhydratedFile): Promise<UUID | null> => {
      if (!pursuitId) {
        return null
      }
      let copied
      try {
        copied = await copyFiles({
          files: [{ file_id: file.id }],
          pursuit_id: pursuitId,
        })
      } catch {
        // The mutation has already reported it.
        return null
      }
      const copyId = copied.results[0]?.library_file_id
      if (!copyId) {
        // A per-file failure comes back inside a successful response, so
        // nothing else has said anything yet.
        toast.error(`Couldn't copy ${file.name} into this pursuit.`)
        return null
      }
      return copyId
    },
    [copyFiles, pursuitId],
  )

  const importFile = useCallback(
    async (file: PotentiallyUnhydratedFile, knownBlob?: Blob) => {
      if (!pursuitId) {
        return
      }
      setIsImporting(true)
      try {
        // Read the bytes before making anything. A file that isn't really a
        // Word document can never be opened, so a document for it would be a
        // permanently broken row in the pursuit's list.
        const cacheKey = fileQueryKeys.docx({ fileId: file.id })
        const opened = queryClient.getQueryData<StoredDocx>(cacheKey)
        // Bytes handed in came off the upload the user just made, and the
        // upload is itself the file's first version. Carrying its id is what
        // stops the preview from reading these bytes as older than the file
        // they were just uploaded as.
        let stored = knownBlob
          ? {
              blob: knownBlob,
              version: null,
              versionId: file.current_version_id ?? null,
            }
          : opened
        if (!stored) {
          try {
            const withUrl = await fetchFile(file.id, user?.accessToken, true)
            const res = await fetch(withUrl.url)
            if (!res.ok) {
              throw new Error(String(res.status))
            }
            stored = {
              blob: await res.blob(),
              version: storedVersionOf(res),
              versionId: withUrl.current_version_id ?? null,
            }
          } catch {
            // Every surface fires this without awaiting a result, so a failure
            // has to say so itself rather than reject into nothing.
            toast.error(`Couldn't open ${file.name}. Please try again.`)
            return
          }
        }
        if (!(await isWordDocument(stored.blob))) {
          toast.error(NOT_A_WORD_DOCUMENT_MESSAGE)
          return
        }
        // Hand the editor the copy already in hand rather than making it fetch
        // the same bytes again on the next page.
        if (stored !== opened) {
          queryClient.setQueryData(cacheKey, stored satisfies StoredDocx)
        }

        let fileId = file.id
        if (file.type !== 'pursuit_file') {
          const copyId = await copyIntoPursuit(file)
          if (!copyId) {
            return
          }
          fileId = copyId
          // Same bytes under the copy's id. The copy is stored on its own, so
          // the version just read belongs to the original; the first save asks
          // for the copy's.
          queryClient.setQueryData(fileQueryKeys.docx({ fileId }), {
            blob: stored.blob,
            version: null,
            versionId: null,
          } satisfies StoredDocx)
        }

        let document
        try {
          document = await openWorkingCopy(fileId)
        } catch {
          // The mutation has already reported it.
          return
        }
        // Held until the next page is up. Releasing at the mutation would put
        // the button back for the length of the navigation, and by then the
        // new document is in the list — so it would flip to its opened state a
        // frame before the dialog goes away.
        await router.push({
          pathname: editorPathname(document.tiptap_id),
          query: { orgId, pursuitId, documentId: document.id },
        })
        onNavigate?.()
      } finally {
        setIsImporting(false)
      }
    },
    [
      copyIntoPursuit,
      openWorkingCopy,
      router,
      orgId,
      pursuitId,
      onNavigate,
      queryClient,
      user?.accessToken,
    ],
  )

  return {
    docxFiles,
    filesAreLoading,
    documentByFile,
    importFile,
    openDocument,
    isImporting,
  }
}
